import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ActiveCartService, CartVoucherService } from '@cart/data-access';
import { CityService } from '@city/feature';
import { OccPromoCouponResponse } from '@core/const';
import { EnvironmentService, PromoCouponService } from '@core/feature';
import { Infoblock, InfoblockButton, InfoblockButtonTypes, PageMeta } from '@main/infoblock/const';
import { InfoblockFacade } from '@main/infoblock/data-access';
import { SnackButton } from '@main/snackbar/const';
import { SnackbarFacade } from '@main/snackbar/data-access';
import { UrlNavigatorService } from '@shared/services';
import { ActivatedRoutesService, Cart, CmsService, Page, Voucher } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user';
import { User } from '@user/const';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class InfoblockService implements OnDestroy {
  private subscription = new Subscription();

  constructor(
    private infoblockFacade: InfoblockFacade,
    private snackbarFacade: SnackbarFacade,
    private cityService: CityService,
    private cartVoucherService: CartVoucherService,
    private environmentService: EnvironmentService,
    private cmsService: CmsService,
    private activatedRoutesService: ActivatedRoutesService,
    private activeCartService: ActiveCartService,
    private urlNavigatorService: UrlNavigatorService,
    private promoCouponService: PromoCouponService,
    private userAccountFacade: UserAccountFacade,
  ) {}

  init(): void {
    if (this.environmentService.isPlatformServer) {
      return;
    }

    return this.subscription.add(
      combineLatest([
        this.getCurrentCityId(),
        this.getCurrentPageId(),
        this.getCurrentCategoryCode(),
        this.getActiveVoucherCodes(),
      ])
        .pipe(
          debounceTime(1000),
          switchMap(([cityId, pageId, categoryCode, voucherCodes]: [string, string, string, string[]]) =>
            this.getFilteredInfoblockItems(cityId, pageId, categoryCode, voucherCodes),
          ),
        )
        .subscribe((items: Infoblock[]) => {
          this.closeAllInfoblocks();

          items.forEach((item) => {
            this.openInfoblock(item);
          });
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getCurrentCityId(): Observable<string> {
    return this.cityService.activeBaseStoreCode$.pipe(filter<string>(Boolean), distinctUntilChanged());
  }

  private getCurrentPageId(): Observable<string> {
    return this.cmsService.getCurrentPage().pipe(
      filter<Page>(Boolean),
      map((page) => page.pageId),
      distinctUntilChanged(),
    );
  }

  private getCurrentCategoryCode(): Observable<string | undefined> {
    return this.activatedRoutesService.routes$.pipe(
      filter<ActivatedRouteSnapshot[]>(Boolean),
      map((routes) => routes[routes.length - 1].paramMap.get('categoryCode')),
      distinctUntilChanged(),
    );
  }

  private getActiveVoucherCodes(): Observable<string[]> {
    return this.activeCartService.getActive().pipe(
      map((cart: Cart) => cart?.appliedVouchers || []),
      map((appliedVouchers: Voucher[]) =>
        appliedVouchers
          .map((voucher) => voucher?.voucherCode)
          .filter(Boolean)
          .sort(),
      ),
      distinctUntilChanged(
        (one, two) => one.length === two.length && one.every((value, index) => value === two[index]),
      ),
    );
  }

  private openInfoblock(infoblock: Infoblock): void {
    this.snackbarFacade.setSnack({
      theme: infoblock.theme,
      delay: Number(infoblock.endTime) - Date.now(),
      title: infoblock.title,
      html: infoblock.text,
      button: this.snackButtonFactory(infoblock.button),
      onClose: () => {
        this.infoblockFacade.closeInfoblocks([infoblock.code]);
      },
      customProps: {
        isInfoblock: true,
      },
    });
  }

  private getFilteredInfoblockItems(
    cityId: string,
    pageId: string,
    categoryCode: string,
    voucherCodes: string[],
  ): Observable<Infoblock[]> {
    return this.infoblockFacade.getInfoblockItems(cityId).pipe(
      withLatestFrom(this.infoblockFacade.getClosedInfoblockIds()),
      map(([items, closed]: [Infoblock[], string[]]) => items.filter((i) => !closed.includes(i.code))),
      map((items: Infoblock[]) => {
        const currentPageMeta: PageMeta = {
          uid: pageId,
          categoryCode,
        };

        return items.filter((item) => {
          if (item.excludedPages.length && this.hasMatch(currentPageMeta, item.excludedPages)) {
            return false;
          }

          if (item.includedPages.length && !this.hasMatch(currentPageMeta, item.includedPages)) {
            return false;
          }

          if (item.button?.promoCode && voucherCodes.includes(item.button.promoCode)) {
            return false;
          }

          if (Number(item.endTime) < Date.now()) {
            return false;
          }

          return true;
        });
      }),
    );
  }

  private hasMatch(item: PageMeta, matchArr: PageMeta[]): boolean {
    return matchArr.some((match) => {
      if (match.uid && match.categoryCode) {
        return match.uid === item.uid && match.categoryCode === item.categoryCode;
      }

      if (match.uid && match.uid === item.uid) {
        return true;
      }

      if (match.categoryCode && match.categoryCode === item.categoryCode) {
        return true;
      }

      return false;
    });
  }

  private closeAllInfoblocks(): void {
    this.snackbarFacade.removeAll((snack) => Boolean(snack.customProps?.isInfoblock));
  }

  private snackButtonFactory(infoblockBtn?: InfoblockButton): SnackButton | undefined {
    if (!infoblockBtn) {
      return undefined;
    }

    if (infoblockBtn.type === InfoblockButtonTypes.STANDARD) {
      return {
        label: infoblockBtn.label,
        onClick: (): void => {
          this.urlNavigatorService.navigate(infoblockBtn.link);
        },
      };
    }

    if (infoblockBtn.type === InfoblockButtonTypes.PROMOCODE) {
      return {
        label: infoblockBtn.label,
        onClick: (): void => {
          this.cartVoucherService.addVoucherToActiveCart(infoblockBtn.promoCode);
        },
      };
    }

    if (infoblockBtn.type === InfoblockButtonTypes.MULTIPROMOCODE) {
      return {
        label: infoblockBtn.label,
        onClick: (): void => {
          this.userAccountFacade
            .get()
            .pipe(
              take(1),
              switchMap((user: User) =>
                this.promoCouponService.requestCoupon(user.phone, infoblockBtn.promoCode).pipe(take(1)),
              ),
            )
            .subscribe((response: OccPromoCouponResponse) => {
              this.cartVoucherService.addVoucherToActiveCart(response.couponId);
            });
        },
      };
    }

    return undefined;
  }
}
